import React, { useState, useMemo } from "react";
import {
    DialogTitle,
    DialogContent,
    Dialog,
    DialogActions,
    Icon,
    IconButton,
    FormControlLabel,
    Checkbox,
    Tooltip,
    Divider,
} from "@material-ui/core";
import {
    ButtonApolloPrimary,
    ButtonApolloSecondary,
} from "../../../Button/Button";
import {
    ALL_PRODUCT_OPTIONS,
} from "../../../../constants/calculatorConstants";
import styled from "styled-components";
import * as constants from "../../../../constants/calculatorConstants"
import { getProductOptionByKey } from "../../../../utils/dependencyUtils";
import { getDistribution, getAvailableProducts } from "../../../../adapters/distributionHelper";
import { getProductDependents, reconcileProductsWithGraph } from "../../../../adapters/dependencyAdapter";

const DialogStyled = styled(Dialog)`
  background: rgba(0, 0, 0, 0.3);

  &.MuiDialog-root.alert .container {
    align-items: center;
  }
`;

const AddProductModal = ({
                             onClose,
                             selectedProducts,
                             onSelectedProductsChange,
                             highLevelParameters,
                             parentExplicitKeys,
                         }: {
    onClose: () => void;
    selectedProducts: ProductOptionInterface[];
    onSelectedProductsChange: (data: ProductOptionInterface[], explicitKeys: string[]) => void;
    highLevelParameters: HighLevelParametersInterface;
    parentExplicitKeys: string[];
}) => {
    // Track explicitly selected products (roots). Auto-added deps are derived.
    const [explicitKeys, setExplicitKeys] = useState<string[]>(
        () => [...parentExplicitKeys]
    );

    const distribution = getDistribution(highLevelParameters.deploymentType);
    const availableProducts = getAvailableProducts(distribution);

    // Derive the full product list: explicit selections + all transitive deps.
    // Same reconciliation runs on URL load in CalculatorForm so the right
    // panel and HW calc stay in sync with what this modal shows.
    const updatedSelectedProducts = useMemo(
        () => reconcileProductsWithGraph(explicitKeys, selectedProducts || [], distribution, ALL_PRODUCT_OPTIONS),
        [explicitKeys, distribution]
    );

    const handleCheckboxChange = (clickedOption: any) => {
        if (!clickedOption) return;
        if (clickedOption.key === 'platform') return; // Never explicitly select platform
        const isExplicit = explicitKeys.includes(clickedOption.key);
        if (isExplicit) {
            // Remove from explicit selections; orphaned deps auto-removed via derivation
            setExplicitKeys(explicitKeys.filter(k => k !== clickedOption.key));
        } else {
            // Add to explicit selections; deps auto-added via derivation
            setExplicitKeys([...explicitKeys, clickedOption.key]);
        }
    };

    const checkedKeys = new Set(updatedSelectedProducts.map((p: any) => p.key));

    const handleAddClick = () => {
        onSelectedProductsChange(updatedSelectedProducts, explicitKeys);
        onClose();
    };

    const isAddDisabled = () => {
        // check if length is different
        if (updatedSelectedProducts.length != selectedProducts.length) {
            return false;
        }
        const updatedSelectedKeys = new Set(updatedSelectedProducts.map(product => product.key));
        const selectedKeys = selectedProducts.map(product => product.key);
        // check if all keys are same
        for (let keys in selectedKeys){
            if(!updatedSelectedKeys.has(selectedKeys[keys])){
                return false;
            }
        }
        return true;
    };

    const getDisabledKeys = (): Set<string> => {
        const disabled = new Set<string>();
        for (const option of ALL_PRODUCT_OPTIONS) {
            // Shared Suite Capabilities is always auto-selected, never user-selectable
            if (option.key === 'platform') {
                disabled.add(option.key);
            }
            if (!availableProducts.has(option.key)) {
                disabled.add(option.key);
            }
            if (option.key === constants.DOCUMENT_UNDERSTANDING_MODERN_PROJECTS_PRODUCT_OPTION.key) {
                const versionYear = parseInt(highLevelParameters.version?.split(".")[0], 10);
                if (versionYear <= 2023) {
                    disabled.add(option.key);
                }
            }
        }
        return disabled;
    };

    const selectableOptions = useMemo(() => {
        const disabled = getDisabledKeys();
        return ALL_PRODUCT_OPTIONS.filter(opt => !disabled.has(opt.key));
    }, [highLevelParameters.deploymentType, highLevelParameters.version, updatedSelectedProducts]);

    const isAllSelected = selectableOptions.every(opt =>
        updatedSelectedProducts.some(p => p.key === opt.key)
    );

    const handleSelectAll = () => {
        if (isAllSelected) {
            // Uncheck all selectable products from explicit keys
            const selectableKeys = new Set(selectableOptions.map(opt => opt.key));
            setExplicitKeys(explicitKeys.filter(k => !selectableKeys.has(k)));
        } else {
            // Add all selectable products to explicit keys (deps resolved via derivation)
            const currentExplicit = new Set(explicitKeys);
            const toAdd = selectableOptions.filter(opt => !currentExplicit.has(opt.key) && opt.key !== 'platform').map(opt => opt.key);
            setExplicitKeys([...explicitKeys, ...toAdd]);
        }
    };

    const generateOptions = () => {
        const selectedKeys = updatedSelectedProducts.map((p: any) => p.key);

        // Platform special-case: check external orchestrator and other services
        let isOtherServiceSelected = updatedSelectedProducts.findIndex(
            (option: any) => {
                return (option.key === constants.BUSINESS_APPS_PRODUCT_OPTION.key
                    || option.key === constants.AUTOMATION_HUB_PRODUCT_OPTION.key
                    || option.key === constants.AUTOMATION_OPS_PRODUCT_OPTION.key
                    || option.key === constants.DATA_SERVICE_PRODUCT_OPTION.key
                    || option.key === constants.PROCESS_MINING_PRODUCT_OPTION.key);
            }) >= 0;
        let isAiCenterSelected = updatedSelectedProducts.findIndex(
            (option: any) => {
                return option.key === constants.AI_CENTER_PRODUCT_OPTION.key;
            }) >= 0;
        let isExternalOrchestratorEnabled = false;
        if (isAiCenterSelected) {
            let aiCenterProduct = updatedSelectedProducts.filter(p => p.key == constants.AI_CENTER_PRODUCT_OPTION.key)[0];
            if (aiCenterProduct.properties !== undefined) {
                isExternalOrchestratorEnabled = !!aiCenterProduct.properties["externalOrchestrator"];
            }
        }

        return ALL_PRODUCT_OPTIONS.map((option) => {
            let isDisabled = false;
            let tooltip: string = "";

            // Generic dependency-based disabling (excluding circular relationships)
            const dependents = getProductDependents(option.key, selectedKeys, distribution);
            if (dependents.length > 0) {
                const dependentNames = dependents.map(
                    (depKey) => getProductOptionByKey(depKey, ALL_PRODUCT_OPTIONS)?.name || depKey
                );
                isDisabled = true;
                tooltip = `${dependentNames.join(", ")} depend on ${option.name}`;
            }

            // Auto-selected products (in selection but not explicit) cannot be unchecked directly
            const isAutoSelected = checkedKeys.has(option.key) && !explicitKeys.includes(option.key);
            if (isAutoSelected) {
                isDisabled = true;
                // tooltip already set by getProductDependents above — don't overwrite
            }

            // Platform special case: allow removal only when external orchestrator enabled and no other services
            if (option.key === constants.PLATFORM_PRODUCT_OPTION.key
                && isExternalOrchestratorEnabled && !isOtherServiceSelected) {
                isDisabled = false;
                tooltip = "";
            }

            // Profile-based disabling: product not available in this distribution
            if (!availableProducts.has(option.key)) {
                isDisabled = true;
                tooltip = `${option.name} is not available on the selected Kubernetes distribution`;
            }

            // DU Modern Projects is not available when AS version is 2023.10
            if (option.key === constants.DOCUMENT_UNDERSTANDING_MODERN_PROJECTS_PRODUCT_OPTION.key) {
                const versionYear = parseInt(highLevelParameters.version?.split(".")[0], 10);
                if (versionYear <= 2023) {
                    isDisabled = true;
                    tooltip = "Document Understanding - Modern Projects is not available for AS version 2023.10 and below";
                }
            }

            // Shared Suite Capabilities: always auto-selected, never user-selectable
            if (option.key === 'platform') {
                isDisabled = true;
                tooltip = "Auto-selected (shared infrastructure)";
            }

            return (
                <div key={option.key}>
                    <Tooltip title={tooltip}>
                        <FormControlLabel
                            disabled={isDisabled}
                            control={
                                <Checkbox
                                    onChange={() => handleCheckboxChange(option)}
                                    checked={checkedKeys.has(option.key)}
                                    name={option.key}
                                />
                            }
                            label={option.name}
                        />
                    </Tooltip>
                </div>
            );
        });
    };

    return (
        <DialogStyled className="alert" open={true} onClose={onClose}>
            <DialogTitle>
                <div className="container">
                    Products
                    <div
                        style={{
                            flex: "1 0 0",
                        }}
                    />
                    <IconButton onClick={onClose}>
                        <Icon>close</Icon>
                    </IconButton>
                </div>
            </DialogTitle>
            <DialogContent>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isAllSelected}
                            indeterminate={!isAllSelected && selectableOptions.some(opt =>
                                updatedSelectedProducts.some(p => p.key === opt.key)
                            )}
                            onChange={handleSelectAll}
                            name="select-all"
                        />
                    }
                    label={<strong>Select All</strong>}
                />
                <Divider />
                {generateOptions()}
            </DialogContent>
            <DialogActions>
                <ButtonApolloSecondary onClick={onClose}>Cancel</ButtonApolloSecondary>
                <ButtonApolloPrimary
                    onClick={handleAddClick}
                    disabled={isAddDisabled()}
                >
                    Add
                </ButtonApolloPrimary>
            </DialogActions>
        </DialogStyled>
    );
};

export default AddProductModal;
